import { formatEatDateTime, khisPeriodLabel, parseKhisPeriod, type KhisPeriod } from '@wodi/domain';
import type { KhisSubmissionState } from '@wodi/shared';
import { Button, Notice, Tag, type TagState } from '@wodi/ui';

import { ApiError } from '../api/client.ts';

// Words and tags shared by the KHIS screens (docs/plans/phase3-khis-and-quality.md §4).
// States are words with shapes (DESIGN.md §7); a failure is the neutral failed tag.

export const SUBMISSION_STATE: Record<KhisSubmissionState, { tag: TagState; words: string }> = {
  prepared: { tag: 'pending', words: 'Waiting for approval' },
  waiting: { tag: 'active', words: 'Waiting to send' },
  sent: { tag: 'done', words: 'Sent to KHIS' },
  failed: { tag: 'failed', words: 'Failed' },
  discarded: { tag: 'neutral', words: 'Discarded' },
  superseded: { tag: 'neutral', words: 'Replaced by a correction' },
};

export function SubmissionTag({ state }: { state: KhisSubmissionState }) {
  return <Tag state={SUBMISSION_STATE[state].tag}>{SUBMISSION_STATE[state].words}</Tag>;
}

/** `Sep 2026` for `202609`. */
export function periodWords(period: string): string {
  const parsed = parseKhisPeriod(period);
  return parsed === null ? period : khisPeriodLabel(parsed);
}

/** `22 Sep 2026 14:05`. */
export function whenWords(iso: string): string {
  return formatEatDateTime(new Date(iso));
}

/** The `count` months ending with `last`, newest first, as `YYYYMM`. */
export function recentPeriods(last: string, count: number): string[] {
  const parsed = parseKhisPeriod(last);
  if (parsed === null) return [];
  const out: string[] = [];
  let p: KhisPeriod = parsed;
  for (let i = 0; i < count; i += 1) {
    out.push(`${String(p.year)}${String(p.month).padStart(2, '0')}`);
    p = p.month === 1 ? { year: p.year - 1, month: 12 } : { year: p.year, month: p.month - 1 };
  }
  return out;
}

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function LoadFailed({
  error,
  what,
  retry,
}: {
  error: unknown;
  what: string;
  retry: () => void;
}) {
  return (
    <Notice
      tone="error"
      action={
        <Button dense onPress={retry}>
          Try again
        </Button>
      }
    >
      {errorText(error, `${what} couldn't be loaded. Try again.`)}
    </Notice>
  );
}
